import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAddPayer, useApiKeys, useClientRole, useCreateApiKey, useCreateWebhook, useDeleteWebhook, usePingWebhook, useProjects, useReplayFailedWebhooks, useReplayWebhookDelivery, useRevokeApiKey, useRotateApiKey, useRotateWebhookSecret, useUpdateWebhook, useWebhookDeliveries, useWebhooks, useColleagues, useInviteColleague, usePayer, usePayerDetails, usePayers, usePrefs, useResendInvitation, useSavePayer, useSavePrefs, useSetDefaultPayer, useUpdateColleague, useUpdatePayer, useWorkspace } from '@/api';
import type { ClientContact, ColleaguesReply, LegalSchema, NotificationPrefs, PayerDetails } from '@/api/types';
import { ApiKeys } from '@/components/ApiKeys';
import { API_DOCS_URL } from '@/lib/apiKeys';
import { WebhookEndpoints, WebhookLog } from '@/components/Webhooks';
import { useLogPaging, WEBHOOK_DOCS_URL } from '@/lib/webhooks';
import { LegalLinks } from '@/components/Layout';
import { RequisitesFields } from '@/components/Requisites';
import { PersonLink } from '@/components/PersonDialog';
import { QueryPending } from '@/components/QueryState';
import { Avatar, PageHeader } from '@/components/ui';
import { clientCan } from '@/lib/access';
import { useContour } from '@/lib/contour';

const roleLabel = { owner: 'Владелец: всё', member: 'Сотрудник: проекты и переписка', accountant: 'Бухгалтер: деньги и документы' } as const;

type EditorProps = { legal: LegalSchema; acts: boolean; initial: PayerDetails; onSave: (p: PayerDetails, done: () => void) => void; pending: boolean; error: unknown; submit: string; savedText?: string };

/** One payer's requisites by the contour's schema, plus the e-mail invoices go to. */
function PayerEditor({ legal, acts, initial, onSave, pending, error, submit, savedText }: EditorProps) {
  const [form, setForm] = useState<PayerDetails>(initial);
  const [saved, setSaved] = useState(false);
  useEffect(() => setForm(initial), [initial]);
  const set = (k: string, v: string) => (setSaved(false), setForm({ ...form, [k]: v }));
  const docs = acts ? 'счетов и актов' : 'счетов';
  return (
    <form
      className="grid grid-cols-1 gap-4 sm:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(form, () => setSaved(true));
      }}
    >
      <RequisitesFields legal={legal} values={form} onChange={set} />
      <label className="block text-sm sm:col-span-2">
        <span className="mb-1.5 block text-muted">Почта для {docs}</span>
        <input className="field" name="email" type="email" value={form.email ?? ''} onChange={(e) => set('email', e.target.value)} />
      </label>
      <div className="flex items-center gap-3 sm:col-span-2">
        <button className="btn-primary" disabled={pending}>
          {submit}
        </button>
        {saved && savedText && <span className="text-sm text-done">{savedText}</span>}
        {!!error && <span className="text-sm text-overdue">{(error as Error).message}</span>}
      </div>
    </form>
  );
}

/** The default payer's details by the contour's schema: ИНН/КПП in RU, country/VAT id/address lines in main. */
export function PayerForm({ legal, acts }: { legal: LegalSchema; acts: boolean }) {
  const payer = usePayer();
  const save = useSavePayer();
  if (!payer.data) return <QueryPending query={payer} />;
  return (
    <PayerEditor
      legal={legal}
      acts={acts}
      initial={payer.data}
      onSave={(p, done) => save.mutate(p, { onSuccess: done })}
      pending={save.isPending}
      error={save.error}
      submit="Сохранить реквизиты"
      savedText={acts ? 'Сохранено. Новые счета и акты — на эти реквизиты.' : 'Сохранено. Новые счета — на эти данные.'}
    />
  );
}

function PayerEdit({ id, legal, acts, onDone }: { id: string; legal: LegalSchema; acts: boolean; onDone: () => void }) {
  const details = usePayerDetails(id);
  const save = useUpdatePayer();
  if (!details.data) return <QueryPending query={details} />;
  return <PayerEditor legal={legal} acts={acts} initial={details.data} onSave={(payer) => save.mutate({ id, payer }, { onSuccess: onDone })} pending={save.isPending} error={save.error} submit="Сохранить реквизиты" />;
}

const EMPTY: PayerDetails = { email: '' };

/** «Плательщики»: the company's legal entities invoices are made out to; one is the default. */
export function Payers({ legal, acts }: { legal: LegalSchema; acts: boolean }) {
  const list = usePayers();
  const [params] = useSearchParams();
  const [open, setOpen] = useState<string | null>(params.get('payer'));
  const add = useAddPayer();
  const setDefault = useSetDefaultPayer();
  const taxLabel = legal.fields.find((f) => f.column === 'tax_id')?.label ?? '';
  if (!list.data) return <QueryPending query={list} />;
  const many = list.data.length > 1;
  return (
    <>
      <ul className="divide-y divide-line border-y border-line">
        {list.data.map((p) => (
          <li key={p.id} className="py-3" data-testid={`payer-${p.id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium">
                  {p.legalName || 'Название не указано'}
                  {many && p.isDefault && <span className="ml-2 text-xs font-normal text-muted">основной</span>}
                </span>
                <span className="block text-xs text-muted">
                  {[p.taxId && `${taxLabel} ${p.taxId}`, p.projects.length ? p.projects.map((x) => x.title).join(', ') : many ? 'Проекты не привязаны' : ''].filter(Boolean).join(' · ')}
                </span>
                {p.missing.length > 0 && <span className="block text-xs text-waiting">Не указан {p.missing.join(', ')}. Счета выставляются и без этого, но лучше дополнить.</span>}
              </span>
              <span className="flex gap-1">
                <button className="btn-ghost h-7 px-2 text-xs" onClick={() => setOpen(open === p.id ? null : p.id)}>
                  {open === p.id ? 'Свернуть' : 'Изменить'}
                </button>
                {!p.isDefault && (
                  <button className="btn-ghost h-7 px-2 text-xs" disabled={setDefault.isPending} onClick={() => setDefault.mutate(p.id)}>
                    Сделать основным
                  </button>
                )}
              </span>
            </div>
            {open === p.id && (
              <div className="mt-4">
                <PayerEdit id={p.id} legal={legal} acts={acts} onDone={() => setOpen(null)} />
              </div>
            )}
          </li>
        ))}
      </ul>
      {setDefault.error && <p className="mt-2 text-sm text-overdue">{(setDefault.error as Error).message}</p>}
      {open === 'new' ? (
        <div className="mt-4">
          <h3 className="mb-3 text-sm font-medium">Новое юрлицо</h3>
          <PayerEditor legal={legal} acts={acts} initial={EMPTY} onSave={(p) => add.mutate(p, { onSuccess: () => setOpen(null) })} pending={add.isPending} error={add.error} submit="Добавить плательщика" />
        </div>
      ) : (
        <button className="btn-ghost mt-3" onClick={() => setOpen('new')}>
          Добавить юрлицо
        </button>
      )}
      <p className="mt-3 text-xs text-muted">Если счета оплачивают разные юрлица, добавьте каждое. Основной плательщик — для проектов, где студия не выбрала другого.</p>
    </>
  );
}

function Team({ canInvite }: { canInvite: boolean }) {
  const { data } = useColleagues();
  const { data: ws } = useWorkspace();
  const invite = useInviteColleague();
  const update = useUpdateColleague();
  const resend = useResendInvitation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<ClientContact['role']>('member');
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState('');
  const fail = (e: Error) => (setNote(''), setProblem(e.message));
  const ok = (r: ColleaguesReply) => (setProblem(''), setNote(r.detail));
  const roles = Object.keys(roleLabel) as ClientContact['role'][];
  return (
    <>
      <ul className="divide-y divide-line border-y border-line">
        {data?.map((c) => {
          const mine = c.person.id === ws?.me.id;
          return (
            <li key={c.email} className="flex flex-wrap items-center gap-3 py-3">
              <Avatar person={c.person} />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium">
                  <PersonLink person={c.person} />
                </span>
                <span className="block text-xs text-muted">
                  {c.email}
                  {!canInvite && `, ${roleLabel[c.role].split(':')[0].toLowerCase()}`}
                </span>
              </span>
              {canInvite && (
                <select
                  className="field h-8 w-56 text-sm"
                  aria-label={`Роль: ${c.person.name}`}
                  value={c.role}
                  onChange={(e) => update.mutate({ id: c.person.id, patch: { role: e.target.value as ClientContact['role'] } }, { onSuccess: ok, onError: fail })}
                >
                  {roles.map((r) => (
                    <option key={r} value={r}>
                      {roleLabel[r]}
                    </option>
                  ))}
                </select>
              )}
              <span className={c.disabled ? 'text-xs text-overdue' : c.invited === 'accepted' ? 'text-xs text-done' : 'text-xs text-waiting'}>{c.disabled ? 'отключён' : c.invited === 'accepted' ? 'в кабинете' : 'приглашён'}</span>
              {canInvite && !mine && (
                <span className="flex gap-1">
                  {c.invited !== 'accepted' && !c.disabled && (
                    <button className="btn-ghost h-7 px-2 text-xs" disabled={resend.isPending} onClick={() => resend.mutate(c.person.id, { onSuccess: ok, onError: fail })}>
                      Отправить приглашение снова
                    </button>
                  )}
                  <button className="btn-ghost h-7 px-2 text-xs" disabled={update.isPending} onClick={() => update.mutate({ id: c.person.id, patch: { disabled: !c.disabled } }, { onSuccess: ok, onError: fail })}>
                    {c.disabled ? 'Вернуть доступ' : 'Отключить'}
                  </button>
                </span>
              )}
            </li>
          );
        })}
      </ul>
      {!canInvite && <p className="mt-3 text-sm text-muted">Пригласить коллег и управлять командой может владелец кабинета компании.</p>}
      {canInvite && <p className="mt-3 text-xs text-muted">Ссылка действует 20 минут. Если человек не успел войти, отправьте приглашение снова.</p>}
      {canInvite && (
        <form
          className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_220px_auto]"
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim() || !/.+@.+\..+/.test(email)) return;
            invite.mutate({ name: name.trim(), email: email.trim(), role }, { onSuccess: (r) => (ok(r), setName(''), setEmail('')), onError: fail });
          }}
        >
          <label>
            <span className="sr-only">Имя нового человека</span>
            <input className="field" placeholder="Имя" autoComplete="off" maxLength={200} value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label>
            <span className="sr-only">Почта нового человека</span>
            <input className="field" placeholder="Почта" type="email" autoComplete="off" maxLength={254} value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label>
            <span className="sr-only">Роль нового человека</span>
            <select className="field" value={role} onChange={(e) => setRole(e.target.value as ClientContact['role'])}>
              {roles.map((r) => (
                <option key={r} value={r}>
                  {roleLabel[r]}
                </option>
              ))}
            </select>
          </label>
          <button className="btn-primary" disabled={invite.isPending}>
            Пригласить
          </button>
        </form>
      )}
      {note && (
        <p role="status" className="mt-2 text-sm text-done">
          {note}
        </p>
      )}
      {problem && (
        <p role="alert" className="mt-2 text-sm text-overdue">
          {problem}
        </p>
      )}
    </>
  );
}

/** «Интеграции»: the owner's keys of the public API (MK42-6384); the section exists while the studio keeps the API on. */
function Integrations() {
  const keys = useApiKeys();
  const { data: projects } = useProjects();
  const create = useCreateApiKey();
  const rotate = useRotateApiKey();
  const revoke = useRevokeApiKey();
  if (!keys.data) return <QueryPending query={keys} />;
  return (
    <>
      <ApiKeys
        keys={keys.data}
        projects={projects ?? []}
        empty="Ключей пока нет. Выпустите ключ для системы, которая будет забирать данные из кабинета или присылать идеи."
        actions={{ issue: create.mutateAsync, rotate: rotate.mutateAsync, revoke: revoke.mutateAsync, pending: create.isPending || rotate.isPending || revoke.isPending }}
      />
      <p className="mt-3 text-xs text-muted">
        <Link className="underline underline-offset-2" to={API_DOCS_URL}>
          Документация API и «Как подключить» 1С, CRM, n8n
        </Link>
        .{' '}
        Ключ видит только данные вашей компании и только то, что разрешают его права.
      </p>
      <Webhooks />
    </>
  );
}

/** «Вебхуки» (MK42-6388): the portal tells the client's server about events; the same owner and switch as the keys. */
function Webhooks() {
  const list = useWebhooks();
  const paging = useLogPaging();
  const log = useWebhookDeliveries(paging.filter);
  const create = useCreateWebhook();
  const update = useUpdateWebhook();
  const remove = useDeleteWebhook();
  const rotate = useRotateWebhookSecret();
  const ping = usePingWebhook();
  const replay = useReplayWebhookDelivery();
  const replayFailed = useReplayFailedWebhooks();
  if (!list.data) return <QueryPending query={list} />;
  const { endpoints, configured } = list.data;
  const events = list.data.events.map((e) => e.type);
  const pending = create.isPending || update.isPending || remove.isPending || rotate.isPending || ping.isPending || replayFailed.isPending;
  return (
    <div data-testid="webhooks">
      <h3 className="mb-1 mt-8 text-sm font-medium">Вебхуки</h3>
      <p className="mb-3 text-sm text-muted">
        Портал сам сообщит вашему серверу, когда выставлен счёт, опубликован отчёт или задача ждёт вас. Подпись — Standard Webhooks,{' '}
        <Link className="underline underline-offset-2" to={WEBHOOK_DOCS_URL}>
          как проверить
        </Link>
        .
      </p>
      {!configured && <p className="mb-3 text-sm text-muted">Вебхуки в этом контуре пока не настроены: напишите студии.</p>}
      <WebhookEndpoints
        endpoints={endpoints}
        events={events}
        empty="Вебхуков пока нет."
        actions={
          configured
            ? {
                create: create.mutateAsync,
                update: (id, patch) => update.mutateAsync({ id, patch }),
                remove: remove.mutateAsync,
                rotate: rotate.mutateAsync,
                ping: ping.mutateAsync,
                replayFailed: (id, since, until) => replayFailed.mutateAsync({ id, since, until }),
                pending,
              }
            : undefined
        }
      />
      {endpoints.length > 0 && (
        <>
          <h4 className="mb-2 mt-6 text-sm font-medium">Журнал доставок</h4>
          <WebhookLog
            page={log.data}
            filter={paging.filter}
            onFilter={paging.setFilter}
            onOlder={() => paging.older(log.data?.nextBefore)}
            onNewer={paging.newer}
            onReplay={replay.mutateAsync}
            replaying={replay.isPending}
            endpoints={endpoints}
            events={events}
          />
        </>
      )}
    </div>
  );
}


function Notifications() {
  const prefs = usePrefs();
  const data = prefs.data;
  const { data: ws } = useWorkspace();
  const save = useSavePrefs();
  if (!data) return <QueryPending query={prefs} />;
  // Channels are the contour's: the RU contour has no Telegram.
  return <PrefsForm data={data} onChange={(p) => save.mutate(p)} telegram={!!ws?.contour.channels.includes('telegram')} acts={!!ws?.contour.legal.acts} />;
}

/** The notification switches themselves: the client's Settings and «Мой профиль» (both sides) share them. */
export function PrefsForm({ data, onChange, telegram, acts, studio }: { data: NotificationPrefs; onChange: (p: NotificationPrefs) => void; telegram: boolean; acts: boolean; studio?: boolean }) {
  const set = (p: Partial<NotificationPrefs>) => onChange({ ...data, ...p });
  return (
    <div className="space-y-3 text-sm">
      <label className="flex items-center gap-2">
        <input type="checkbox" checked={data.email} onChange={(e) => set({ email: e.target.checked })} /> {studio ? 'Письма о том, что ждёт вас в кабинете' : `Письма о том, что ждут вас, ${acts ? 'счетах и актах' : 'счетах'}`}
      </label>
      {telegram && (
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={data.telegram} onChange={(e) => set({ telegram: e.target.checked })} /> Telegram: новые сообщения команды
        </label>
      )}
      <div className="flex items-center gap-2">
        <span className="text-muted">Сводка</span>
        <div className="flex rounded bg-sunken p-0.5" role="radiogroup" aria-label="Сводка">
          {(
            [
              ['off', 'не нужна'],
              ['daily', 'каждое утро'],
              ['weekly', 'по понедельникам'],
            ] as const
          ).map(([v, l]) => (
            <button key={v} role="radio" aria-checked={data.digest === v} onClick={() => set({ digest: v })} className={data.digest === v ? 'rounded-sm bg-canvas px-2.5 py-1.5 sm:py-1 font-medium shadow-hair' : 'px-2.5 py-1.5 sm:py-1 text-muted'}>
              {l}
            </button>
          ))}
        </div>
      </div>
      <p className="text-xs text-muted">Сводка собирает всё за период в одно письмо вместо потока уведомлений.</p>
    </div>
  );
}

export function Settings() {
  const role = useClientRole();
  const { data: ws } = useWorkspace();
  const contour = useContour();
  const acts = !!contour?.legal.acts;
  return (
    <>
      <PageHeader title="Компания и команда" description={`${acts ? 'Реквизиты для счетов и актов' : 'Данные компании для счетов'}, люди вашей компании в кабинете и уведомления.`} />
      <div className="max-w-3xl space-y-12">
        {clientCan(role, 'money') && contour && (
          <section>
            <h2 className="mb-1 text-sm font-semibold">Плательщики</h2>
            <p className="mb-4 text-sm text-muted">{contour.legal.payerTitle}: на кого выставляются счета{acts ? ' и акты' : ''}.</p>
            <Payers legal={contour.legal} acts={acts} />
          </section>
        )}
        <section>
          <h2 className="mb-4 text-sm font-semibold">Люди в кабинете</h2>
          <Team canInvite={clientCan(role, 'admin')} />
        </section>
        {role === 'owner' && ws?.apiEnabled && (
          <section>
            <h2 className="mb-1 text-sm font-semibold">Интеграции</h2>
            <p className="mb-4 text-sm text-muted">Ключи API для ваших систем: бухгалтерии, CRM, отчётов. Выпускает и отзывает владелец кабинета.</p>
            <Integrations />
          </section>
        )}
        <section>
          <h2 className="mb-4 text-sm font-semibold">Уведомления</h2>
          <Notifications />
        </section>
        {(contour?.docs?.offer || contour?.docs?.privacy) && (
          <section>
            <h2 className="mb-4 text-sm font-semibold">Документы</h2>
            <p className="text-sm text-muted">
              <LegalLinks />
            </p>
          </section>
        )}
      </div>
    </>
  );
}
